import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { imgUrl, money } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export function Eyebrow({ children, light }) {
  return <p className={`text-xs font-bold uppercase tracking-[0.2em] ${light ? "text-emerald-400" : "text-emerald-700"}`}>{children}</p>;
}

export function SectionTitle({ eyebrow, title, text, light }) {
  return (
    <div className="max-w-2xl">
      {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
      <h2 className={`mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl ${light ? "text-white" : "text-slate-900"}`}>{title}</h2>
      {text && <p className={`mt-4 text-base leading-relaxed md:text-lg ${light ? "text-slate-300" : "text-slate-600"}`}>{text}</p>}
    </div>
  );
}

export function ProductCard({ p }) {
  const { settings } = useSettings();
  return (
    <div data-testid={`product-card-${p.id}`} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-600/40 hover:shadow-xl hover:shadow-emerald-900/10">
      <Link to={`/products/${p.id}`} className="relative block aspect-[4/3] overflow-hidden bg-slate-100">
        {p.images?.[0] && <img src={imgUrl(p.images[0])} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
        <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-xs font-bold uppercase tracking-wider text-slate-800">{p.mower_type}</span>
      </Link>
      <div className="flex flex-1 flex-col p-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{p.brand} · {p.model}</p>
        <h3 className="mt-1.5 font-display text-xl font-bold text-slate-900">{p.name}</h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-600">{p.description}</p>
        <div className="mt-5 flex items-end justify-between border-t border-slate-100 pt-5">
          <div>
            <p className="text-xs text-slate-500">Retail price</p>
            <p className="font-display text-2xl font-bold text-slate-900">{money(p.retail_price)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Home trial fee</p>
            <p className="font-display text-2xl font-bold text-emerald-700">{money(p.trial_fee ?? settings.trial_fee)}</p>
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <Link to={`/book?product=${p.id}`} data-testid={`product-book-${p.id}`} className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full bg-emerald-700 text-sm font-bold text-white transition-colors hover:bg-emerald-800">Try it for {money(p.trial_fee ?? settings.trial_fee)} <ArrowRight className="h-4 w-4" /></Link>
          <Link to={`/products/${p.id}`} data-testid={`product-details-${p.id}`} className="flex h-11 items-center rounded-full border border-slate-300 px-4 text-sm font-semibold text-slate-800 hover:border-emerald-600">Details</Link>
        </div>
      </div>
    </div>
  );
}

export function PageHero({ eyebrow, title, text }) {
  return (
    <section className="relative overflow-hidden bg-[#0B130E] text-white">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(90deg,#fff 0 60px,transparent 60px 120px)" }} />
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
        <Eyebrow light>{eyebrow}</Eyebrow>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">{title}</h1>
        {text && <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">{text}</p>}
      </div>
    </section>
  );
}

export function FinalCta() {
  const { settings } = useSettings();
  return (
    <section className="bg-emerald-700">
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 py-16 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">Ready to see how it cuts on your grass?</h2>
          <p className="mt-3 max-w-xl text-emerald-50">Request a {money(settings.trial_fee)} home trial. We'll confirm eligibility and availability before anything is scheduled or charged.</p>
        </div>
        <Link to="/book" data-testid="final-cta-book-button" className="inline-flex h-14 shrink-0 items-center gap-2 rounded-full bg-white px-8 text-base font-bold text-emerald-800 transition-transform hover:scale-[1.03]">Book My {money(settings.trial_fee).replace(".00", "")} Trial <ArrowRight className="h-5 w-5" /></Link>
      </div>
    </section>
  );
}
